import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Sparkles,
  Phone,
  MessageCircle,
  MessageSquare,
  Mail,
  StickyNote,
  Calendar,
  TrendingUp,
  RefreshCw,
  Zap,
  ChevronRight,
  Send,
  AlertCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { coachApi, leadsApi } from "@/lib/crmApi";

const cleanPhone = (p = "") => p.replace(/[\s().-]/g, "");
const waLink = (phone, msg) =>
  `https://wa.me/${cleanPhone(phone).replace(/^\+?/, "")}${
    msg ? `?text=${encodeURIComponent(msg)}` : ""
  }`;

const CHANNEL_META = {
  appel:        { Icon: Phone,         label: "Appel",        tone: "bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300" },
  whatsapp:     { Icon: MessageCircle, label: "WhatsApp",     tone: "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300" },
  email:        { Icon: Mail,          label: "Email",        tone: "bg-violet-50 text-violet-700 dark:bg-violet-950/40 dark:text-violet-300" },
  visite:       { Icon: Calendar,      label: "Visite",       tone: "bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300" },
  note_interne: { Icon: StickyNote,    label: "Note interne", tone: "bg-zinc-100 text-zinc-700 dark:bg-zinc-800/60 dark:text-zinc-300" },
  sms:          { Icon: MessageSquare, label: "SMS",          tone: "bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300" },
};

const URGENCY_META = {
  now:        { label: "À FAIRE MAINTENANT", tone: "bg-red-500 text-white" },
  today:      { label: "AUJOURD'HUI",        tone: "bg-amber-500 text-white" },
  this_week:  { label: "CETTE SEMAINE",      tone: "bg-zinc-500 text-white" },
};

function ProbabilityRing({ value = 0 }) {
  const radius = 14;
  const circ = 2 * Math.PI * radius;
  const dash = (value / 100) * circ;
  const tone =
    value >= 70 ? "text-emerald-500" :
    value >= 40 ? "text-amber-500" :
    "text-zinc-400";
  return (
    <div className="relative h-9 w-9">
      <svg viewBox="0 0 36 36" className="h-9 w-9 -rotate-90">
        <circle cx="18" cy="18" r={radius} fill="none" stroke="currentColor" strokeWidth="3" className="text-muted opacity-40" />
        <circle
          cx="18" cy="18" r={radius}
          fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round"
          strokeDasharray={`${dash} ${circ}`}
          className={cn("transition-all duration-500", tone)}
        />
      </svg>
      <span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold tabular-nums">
        {value}%
      </span>
    </div>
  );
}

function DraftDialog({ open, onOpenChange, action, lead, onSent }) {
  const [draft, setDraft] = useState(action?.draft || "");
  useEffect(() => { setDraft(action?.draft || ""); }, [action]);

  if (!action) return null;

  const sendWhatsApp = async () => {
    window.open(waLink(lead.telephone, draft), "_blank", "noopener");
    try {
      await leadsApi.addAction(lead.id, {
        type: "note",
        description: `WhatsApp envoyé via SOGI Coach : ${action.title}`,
      });
      onSent?.();
      toast.success("Message envoyé · interaction enregistrée");
    } catch {}
    onOpenChange(false);
  };

  const sendEmail = async () => {
    if (!lead.email) {
      toast.error("Aucun email renseigné pour ce lead");
      return;
    }
    const subjectMatch = draft.match(/^Objet\s*:\s*(.+)/im);
    const subject = subjectMatch ? subjectMatch[1].trim() : action.title;
    const body = subjectMatch ? draft.replace(/^Objet\s*:.+\n+/i, "") : draft;
    window.location.href = `mailto:${lead.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    try {
      await leadsApi.addAction(lead.id, {
        type: "email",
        description: `Email envoyé : ${subject}`,
      });
      onSent?.();
    } catch {}
    onOpenChange(false);
  };

  const channel = action.channel;
  const sendBtn =
    channel === "whatsapp" ? { label: "Envoyer via WhatsApp", icon: MessageCircle, onClick: sendWhatsApp } :
    channel === "email"    ? { label: "Ouvrir dans Mail",     icon: Mail,          onClick: sendEmail } :
    null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-violet-600" />
            {action.title}
          </DialogTitle>
          <DialogDescription>
            Modifiez le brouillon avant d'envoyer. Le coach s'inspire du contexte de la fiche.
          </DialogDescription>
        </DialogHeader>
        <Textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          rows={10}
          className="font-mono text-xs leading-relaxed"
          data-testid="coach-draft-textarea"
        />
        <div className="flex items-center justify-between text-[11px] text-muted-foreground">
          <span>{draft.length} caractères</span>
          <span>{action.why}</span>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Annuler</Button>
          {sendBtn && (
            <Button onClick={sendBtn.onClick} className="gap-2 bg-emerald-600 text-white hover:bg-emerald-700" data-testid="coach-send-btn">
              <sendBtn.icon className="h-4 w-4" />
              {sendBtn.label}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ActionRow({ action, lead, onExecute, primary = false }) {
  const meta = CHANNEL_META[action.channel] || CHANNEL_META.note_interne;
  const urg = URGENCY_META[action.urgency] || URGENCY_META.this_week;
  const Icon = meta.Icon;

  const directActions = {
    appel: () => { window.location.href = `tel:${cleanPhone(lead.telephone)}`; },
    visite: () => { /* could open a scheduling sheet */ },
    note_interne: async () => {
      try {
        await leadsApi.addAction(lead.id, { type: "note", description: action.title });
        toast.success("Note enregistrée");
        onExecute?.();
      } catch {
        toast.error("Erreur lors de l'enregistrement");
      }
    },
  };

  const handleClick = () => {
    if (action.draft) {
      onExecute?.(action);
    } else if (directActions[action.channel]) {
      directActions[action.channel]();
    } else {
      onExecute?.(action);
    }
  };

  return (
    <div
      data-testid={`coach-action-${action.urgency}`}
      className={cn(
        "rounded-lg border p-3 transition-colors",
        primary
          ? "border-violet-200 bg-violet-50 dark:border-violet-900/50 dark:bg-violet-950/30"
          : "border-border/60 bg-card hover:bg-muted/30"
      )}
    >
      <div className="flex items-start gap-2">
        <span className={cn("flex h-7 w-7 flex-none items-center justify-center rounded-md", meta.tone)}>
          <Icon className="h-3.5 w-3.5" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className={cn("rounded px-1.5 py-0.5 text-[9px] font-bold tracking-wide", urg.tone)}>
              {urg.label}
            </span>
          </div>
          <h4 className="font-heading mt-1 text-sm font-bold leading-tight">{action.title}</h4>
          <p className="mt-1 text-[11px] leading-snug text-muted-foreground">{action.why}</p>
        </div>
      </div>
      {action.draft && primary && (
        <div className="mt-3 rounded-md bg-background/60 p-2.5 text-[11px] italic leading-relaxed">
          "{action.draft.length > 140 ? action.draft.slice(0, 140) + "…" : action.draft}"
        </div>
      )}
      <div className="mt-2 flex justify-end">
        <Button
          size="sm"
          variant={primary ? "default" : "ghost"}
          className={cn("gap-1.5 h-7 text-xs", primary && "bg-violet-600 text-white hover:bg-violet-700")}
          onClick={handleClick}
          data-testid={`coach-execute-${action.channel}`}
        >
          {action.draft ? "Préparer le message" : "Faire maintenant"}
          <ChevronRight className="h-3 w-3" />
        </Button>
      </div>
    </div>
  );
}

export default function CoachPanel({ lead, onAfterAction }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [draftOpen, setDraftOpen] = useState(false);
  const [activeAction, setActiveAction] = useState(null);

  const load = async ({ force = false } = {}) => {
    if (!lead?.id) return;
    setLoading(true);
    setError(null);
    try {
      const d = await coachApi.nextActions(lead.id, { force });
      setData(d);
    } catch (e) {
      setError(e?.response?.data?.detail || "Coach indisponible");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lead?.id]);

  const openDraft = (action) => {
    setActiveAction(action);
    setDraftOpen(true);
  };

  if (!lead) return null;

  return (
    <>
      <aside
        data-testid="coach-panel"
        className="space-y-3 rounded-2xl border border-violet-200/60 bg-gradient-to-br from-violet-50 to-blue-50 p-4 shadow-soft dark:border-violet-900/40 dark:from-violet-950/30 dark:to-blue-950/30"
      >
        <header className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-violet-600 text-white shadow-soft">
            <Sparkles className="h-4 w-4" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="font-heading text-sm font-bold leading-none">SOGI Coach</h3>
            <p className="mt-0.5 text-[10px] uppercase tracking-wider text-muted-foreground">
              Assistant commercial IA
              {data?.source === "ai" && " · Claude"}
              {data?.source?.startsWith("fallback") && " · mode dégradé"}
              {data?.cached && " · cache"}
            </p>
          </div>
          <ProbabilityRing value={data?.closing_probability || 0} />
          <Button
            size="icon"
            variant="ghost"
            className="h-7 w-7"
            onClick={() => load({ force: true })}
            data-testid="coach-refresh-btn"
            title="Régénérer"
            disabled={loading}
          >
            <RefreshCw className={cn("h-3.5 w-3.5", loading && "animate-spin")} />
          </Button>
        </header>

        {data?.closing_reason && (
          <p className="rounded-md bg-background/40 px-3 py-1.5 text-[11px] italic text-muted-foreground">
            <TrendingUp className="mr-1 inline-block h-3 w-3" />
            {data.closing_reason}
          </p>
        )}

        {loading && !data ? (
          <div className="space-y-2">
            <div className="h-24 animate-shimmer rounded-lg" />
            <div className="h-16 animate-shimmer rounded-lg" />
            <div className="h-16 animate-shimmer rounded-lg" />
          </div>
        ) : error ? (
          <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-800 dark:border-amber-900/40 dark:bg-amber-950/40 dark:text-amber-300">
            <AlertCircle className="mr-1 inline-block h-3.5 w-3.5" />
            {error}
            <button
              onClick={() => load()}
              className="ml-2 font-semibold underline"
            >
              Réessayer
            </button>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-muted-foreground">
              <Zap className="h-3 w-3" />
              Prochaines actions
            </div>
            {(data?.actions || []).length === 0 ? (
              // No actions returned by the coach. This usually happens when
              // the lead is too new (no signal yet) or marked as terminal
              // (gagné/perdu). Don't leave the user staring at empty boxes.
              <div
                className="rounded-lg border border-dashed border-violet-300/60 bg-background/40 p-3 text-xs text-muted-foreground dark:border-violet-900/40"
                data-testid="coach-empty"
              >
                <p className="font-medium text-foreground/80">
                  Aucune action recommandée pour le moment.
                </p>
                <p className="mt-1">
                  {data?.closing_probability === 0
                    ? "Le score de qualité du lead est encore trop faible pour générer des recommandations. Enregistre une première interaction (appel, WhatsApp, note) ou attends une réponse pour activer le coach."
                    : "Le coach n'a pas trouvé d'action évidente. Clique sur le bouton ↻ pour relancer une analyse."}
                </p>
              </div>
            ) : (
              (data?.actions || []).map((a, i) => (
                <ActionRow
                  key={i}
                  action={a}
                  lead={lead}
                  primary={i === 0}
                  onExecute={openDraft}
                />
              ))
            )}
          </div>
        )}
      </aside>

      <DraftDialog
        open={draftOpen}
        onOpenChange={setDraftOpen}
        action={activeAction}
        lead={lead}
        onSent={() => {
          onAfterAction?.();
          // Drop AI cache so next view reflects new state
          coachApi.clearCache(lead.id).catch(() => {});
        }}
      />
    </>
  );
}
